import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaFacebook, FaInstagram, FaLinkedin, FaTwitter, FaYoutube } from 'react-icons/fa';
import { advisoryBoardService } from '../services';
import { resolveMediaUrl } from '../services/api';
import PageBreadcrumb from '../components/PageBreadcrumb';

const About = () => {
    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(true);

    useEffect(() => {
        const fetchMembers = async () => {
            try {
                const response = await advisoryBoardService.getAll({ per_page: 6 });
                setMembers(response.data.data || []);
            } catch (error) {
                console.error('Error fetching advisory members:', error);
            } finally {
                setLoadingMembers(false);
            }
        };

        fetchMembers();
    }, []);

    const socialIcons = {
        facebook: FaFacebook,
        instagram: FaInstagram,
        twitter: FaTwitter,
        linkedin: FaLinkedin,
        youtube: FaYoutube,
    };

    return (
        <>
            <Helmet>
                <title>About Us - YAP NGO</title>
                <meta name="description" content="Learn about Youth Association of Pakistan, our mission, vision, and leadership." />
            </Helmet>

            <div className="bg-gray-50">
                <PageBreadcrumb title="About YAP" description="Empowering Youth, Building Communities" items={[{ label: 'About' }]} />

                <section className="py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                            <div>
                                <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Mission</h2>
                                <p className="text-gray-600 text-lg leading-relaxed mb-4">
                                    To empower youth across Pakistan by providing quality education, skill development training, and creating sustainable livelihood opportunities that contribute to personal growth and community development.
                                </p>
                                <p className="text-gray-600 text-lg leading-relaxed">
                                    We believe that youth are the backbone of any nation, and investing in their development is investing in the future of Pakistan.
                                </p>
                            </div>
                            <div>
                                <h2 className="text-3xl font-bold text-gray-800 mb-4">Our Vision</h2>
                                <p className="text-gray-600 text-lg leading-relaxed mb-4">
                                    A prosperous Pakistan where every young person, regardless of their socioeconomic background, has access to quality education and opportunities to develop their potential.
                                </p>
                                <p className="text-gray-600 text-lg leading-relaxed">
                                    We envision a society where youth lead positive change and contribute meaningfully to their communities and the nation.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="py-16 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <h2 className="text-3xl font-bold text-gray-800 mb-8">Our History</h2>
                        <div className="space-y-8">
                            {[['2015', 'Foundation', 'YAP was founded with a mission to address the educational and skill gaps affecting Pakistani youth.'], ['2017', 'Expansion', 'Expanded operations to multiple provinces including Sindh, KPK, and Punjab, reaching thousands of youth.'], ['2020', 'Digital Transformation', 'Launched digital learning initiatives to reach underserved communities during challenging times.'], ['2024', 'Current Impact', 'Operating in 5 provinces with over 50,000 beneficiaries and growing.']].map((item) => (
                                <div key={item[0]} className="flex gap-4">
                                    <div className="flex-shrink-0"><div className="flex items-center justify-center h-12 w-12 rounded-md bg-emerald-500 text-white">{item[0]}</div></div>
                                    <div><h3 className="text-xl font-semibold text-gray-800">{item[1]}</h3><p className="text-gray-600 mt-2">{item[2]}</p></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <h2 className="text-3xl font-bold text-gray-800 mb-12 text-center">Advisory Members</h2>
                        {loadingMembers ? (
                            <div className="py-12 text-center"><div className="inline-block h-12 w-12 animate-spin rounded-full border-b-2 border-emerald-600"></div></div>
                        ) : (
                            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                                {members.map((member) => (
                                    <div key={member.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
                                        <div className="h-48 bg-gradient-to-r from-emerald-400 to-emerald-600">
                                            {member.photo ? (
                                                <img src={resolveMediaUrl(member.photo)} alt={member.name} className="h-full w-full object-cover" />
                                            ) : null}
                                        </div>
                                        <div className="p-6 text-center">
                                            <h3 className="text-xl font-bold text-gray-800">{member.name}</h3>
                                            <p className="text-emerald-600 font-semibold">{member.position}</p>
                                            <div className="mt-3 flex items-center justify-center gap-3">
                                                {Object.entries(member.social_links || {}).map(([key, url]) => {
                                                    if (!url || !socialIcons[key]) return null;
                                                    const Icon = socialIcons[key];
                                                    return <a key={key} href={url} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-emerald-600"><Icon /></a>;
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                <section className="py-16 bg-emerald-600 text-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
                            <div><h3 className="text-4xl font-bold">50,000+</h3><p className="text-emerald-100 mt-2">Youth Beneficiaries</p></div>
                            <div><h3 className="text-4xl font-bold">25+</h3><p className="text-emerald-100 mt-2">Active Projects</p></div>
                            <div><h3 className="text-4xl font-bold">5</h3><p className="text-emerald-100 mt-2">Provinces Covered</p></div>
                            <div><h3 className="text-4xl font-bold">95%</h3><p className="text-emerald-100 mt-2">Success Rate</p></div>
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
};

export default About;
